import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@/i18n/useTranslation';
import { isoToUs, maskUsDate, usToIso } from '@/shared/usDate';

import { DateCalendar } from './DateCalendar';

export interface DateFieldProps {
  readonly id: string;
  readonly label: string;
  /** ISO `YYYY-MM-DD`, or empty. The model never holds the displayed form. */
  readonly value: string;
  /**
   * Emits ISO when the eight digits are a real date on or after `min`, and the raw masked text while they
   * are not.
   */
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly error?: string;
  /** Draws the frame's red asterisk and sets `required`. See `Field` for why both. */
  readonly required?: boolean;
  /**
   * The earliest day the candidate may ENTER, ISO `YYYY-MM-DD`. Empty is no floor.
   *
   * **It binds what is entered, never what is held** [decision 0180; RK-037, Operator, 2026-10-19]. The
   * calendar greys out every earlier day, and a complete date TYPED before it leaves as the masked text
   * rather than ISO — the same way `02/31/2026` does — so the screen's own `YYYY-MM-DD` check marks it and
   * holds Continue. One rule, here, for both screens.
   *
   * ⚠️ **A value that arrives through `value` is never measured against it.** A returning candidate's record
   * carries an availability date entered months ago; it is shown, stays selected in the picker and saves
   * untouched, because nothing the candidate did produced it. Only `handleChange` applies the floor, and the
   * api's tolerance of a past date stays load-bearing for the same reason [0180]: *"you should not pick an
   * old date, but you can have an old date."*
   *
   * It is not an attribute on the input below, because there is nothing to put it on: the control is a
   * masked `type="text"` (see above), and HTML ignores `min` on a text input entirely. A dead attribute
   * there would read as a constraint the browser was enforcing.
   */
  readonly min?: string;
}

/**
 * A date the candidate reads as `MM/DD/YYYY` [Operator, 2026-09-07].
 *
 * **Why this is not `<input type="date">`.** That control's displayed order comes from the browser's own
 * locale — Chrome ignores `lang`, Firefox follows the OS regional setting, and no attribute or CSS changes
 * it. On a US-configured kiosk it happens to render MM/DD/YYYY, but that is the device agreeing rather than
 * the product deciding, and one device imaged with a different regional setting silently shows a different
 * order on a form about when somebody starts work. A masked text input renders what it is told, everywhere.
 *
 * **The native popover it replaced is built back as `DateCalendar`**, so nothing is lost: the field can be
 * typed OR picked, and both write ISO through the same `onChange`. Kept from the native control: the label
 * association, `aria-invalid`, the described-by wiring, and a numeric keypad on a touch device.
 *
 * The model stays ISO on both sides of this component. Only the eight digits between them are US-ordered.
 */
export function DateField({
  id,
  label,
  value,
  onChange,
  hint,
  error,
  required,
  min,
}: DateFieldProps) {
  const t = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  /*
   * Its own display state, because a half-typed date has no ISO value to be derived from. `09/1` is a
   * legitimate thing to be looking at mid-keystroke and there is no way to store it in the model, so the
   * component owns what is on screen and the model owns what is settled.
   */
  const [display, setDisplay] = useState(() => isoToUs(value));

  /*
   * Re-sync when the model changes UNDER the component — the returning screen fills the record in after its
   * fetch resolves, which is several renders after the first.
   *
   * The ref is what distinguishes "the record arrived" from "the candidate typed". Only a value this
   * component did NOT emit rewrites the display; without that test the effect fires on every keystroke and
   * stamps the model back over what is being typed, which reads as a field that will not accept input.
   */
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setDisplay(isoToUs(value));
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const masked = maskUsDate(event.target.value);
    setDisplay(masked);

    /*
     * A complete, REAL date on or after the floor leaves as ISO. Anything else leaves as the masked text,
     * which fails the screen's `YYYY-MM-DD` check and is marked — so `02/31/2026`, a half-typed date and a
     * day before `min` are all refused rather than one of them travelling as an empty field that looks like
     * it was never filled in, or as a past start date the calendar would never have offered [RK-037].
     *
     * The comparison is on the strings: both are `YYYY-MM-DD`, which sorts chronologically, so it needs no
     * `Date` and cannot re-acquire the timezone bug `todayIso` records.
     */
    const iso = usToIso(masked);
    /*
     * An empty `min` is no floor: no ISO date sorts before the empty string.
     *
     * `iso !== value`: a keystroke that leaves the record's own date exactly as it was — a ninth digit the
     * mask drops, a slash it puts straight back — re-emits what is already HELD, and is not a new entry.
     * Without it, tapping into a returning candidate's untouched past date and pressing one key refused it.
     * Clearing the field and typing the same past date again still arrives over a partial value, so it is
     * still refused.
     */
    const beforeFloor = iso !== '' && iso !== value && min !== undefined && iso < min;
    const next = iso === '' || beforeFloor ? masked : iso;
    emitted.current = next;
    onChange(next);
  };

  /*
   * Picking closes the calendar and returns the cursor to the field. Returning focus is not a nicety: the
   * calendar is removed from the DOM on close, and focus that was inside it would fall back to `<body>`,
   * which drops a keyboard user out of the form entirely.
   */
  const pick = useCallback(
    (iso: string) => {
      emitted.current = iso;
      setDisplay(isoToUs(iso));
      onChange(iso);
      setCalendarOpen(false);
      input.current?.focus();
    },
    [onChange],
  );

  const closeCalendar = useCallback(() => {
    setCalendarOpen(false);
  }, []);

  const described = [
    ...(hint === undefined ? [] : [`${id}-hint`]),
    ...(error === undefined ? [] : [`${id}-error`]),
  ];

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-center gap-1">
        <label
          className="text-base font-semibold leading-5 text-[var(--foreground)]"
          htmlFor={id}
          id={`${id}-label`}
        >
          {label}
        </label>
        {required === true && (
          <span aria-hidden="true" className="text-base leading-5 text-[var(--destructive)]">
            *
          </span>
        )}
      </div>
      <div className="relative w-full">
        <input
          aria-describedby={described.length > 0 ? described.join(' ') : undefined}
          aria-invalid={error === undefined ? undefined : true}
          autoComplete="off"
          className={`kiosk-focusable h-10 w-full rounded-[var(--radius-xs)] bg-[var(--card)] p-2 pr-11 text-base leading-6 text-[var(--foreground)] ring-1 ring-inset placeholder:text-[var(--subtle-foreground)] ${
            error === undefined ? 'ring-[var(--border-subtle)]' : 'ring-[var(--error-border)]'
          }`}
          id={id}
          // `numeric` and not `tel`: the slashes are inserted for the candidate, so the keypad never needs a
          // key they would have to hunt for.
          inputMode="numeric"
          onChange={handleChange}
          placeholder="MM/DD/YYYY"
          ref={input}
          required={required}
          spellCheck={false}
          type="text"
          value={display}
        />
        <button
          aria-expanded={calendarOpen}
          aria-haspopup="dialog"
          aria-label={calendarOpen ? t('calendar.close') : t('calendar.open')}
          className="kiosk-focusable absolute right-0 top-0 flex h-10 w-11 items-center justify-center rounded-[var(--radius-xs)] text-[var(--foreground)]"
          onClick={() => {
            setCalendarOpen((open) => !open);
          }}
          ref={toggle}
          type="button"
        >
          {/*
            The frame's vector, not an emoji [`.🔒master / Icon / calendar`, 24×24 box, 18×20 glyph,
            stroke `#8f8d8b` width 2]. It was `📅`, which on Windows renders Segoe UI Emoji's calendar
            showing a hardcoded red "July 17" — a date, on the control for choosing a date, that is never
            the one selected. Decorative: the button carries its purpose in `aria-label`.
          */}
          <svg
            aria-hidden="true"
            className="h-5 w-[18px]"
            fill="none"
            stroke="var(--icon-outline)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 18 20"
          >
            <rect height="15" rx="2" width="16" x="1" y="4" />
            <path d="M5 1v4M13 1v4M1 9h16" />
          </svg>
        </button>
        {calendarOpen && (
          <DateCalendar
            ignore={toggle}
            labelledBy={`${id}-label`}
            min={min}
            onClose={closeCalendar}
            onSelect={pick}
            value={value}
          />
        )}
      </div>
      {hint !== undefined && (
        <span className="text-sm leading-5 text-[var(--subtle-foreground)]" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span className="text-sm leading-5 text-[var(--error)]" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
